/**
 * The pure half of web's reads of Anysola Account's public endpoints for the
 * home page's Betas section and the help page's assistant button
 * (`services/web/src/lib/account-client.ts`). Contract: Account's
 * `GET /api/public/betas` and `GET /api/public/support`
 * (`services/account/docs/web-content-api.md`).
 */

export type PublicBeta = {
  slug: string
  name: string
  description: string
  status: "open" | "closed"
  closesAt: string | null
  /** Account's `applicationsOpen` when it answered; recomputed from `closesAt` by `betaAcceptsApplications`. */
  applicationsOpen: boolean
  /** Where to read about it and apply, on Account. */
  url: string | null
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null
}

/**
 * The published programs from `{ betas: [...] }`, or `null` when the body is
 * not that shape (an unreadable answer is a failure, so the page keeps its
 * last good copy). A row without a slug or a name is dropped, not trusted.
 */
export function parsePublicBetas(body: unknown): PublicBeta[] | null {
  const betas = (body as { betas?: unknown } | null)?.betas
  if (!Array.isArray(betas)) return null
  const programs: PublicBeta[] = []
  for (const row of betas) {
    if (!row || typeof row !== "object") continue
    const beta = row as Record<string, unknown>
    const slug = str(beta.slug)
    const name = str(beta.name)
    if (!slug || !name) continue
    programs.push({
      slug,
      name,
      description: typeof beta.description === "string" ? beta.description : "",
      status: beta.status === "open" ? "open" : "closed",
      closesAt: str(beta.closesAt),
      applicationsOpen: beta.applicationsOpen === true,
      url: str(beta.url),
    })
  }
  return programs
}

/** Open, and the deadline (if any) not passed at `now`: Account computed it at read time, which may be a cache ago. */
export function betaAcceptsApplications(
  beta: PublicBeta,
  now = new Date()
): boolean {
  if (beta.status !== "open" || !beta.applicationsOpen) return false
  if (beta.closesAt === null) return true
  const closes = new Date(beta.closesAt)
  return Number.isNaN(closes.getTime()) || closes > now
}

/** A program's page on Account: the `url` Account sent, else `<account>/betas/<slug>`. */
export function betaHref(beta: PublicBeta, accountUrl: string): string {
  return beta.url ?? `${accountUrl}/betas/${encodeURIComponent(beta.slug)}`
}

/** `{ assistant: { tickets: boolean } }`; anything else, and any failure, reads as "no assistant". */
export function parseSupportAssistant(body: unknown): boolean | null {
  const assistant = (body as { assistant?: { tickets?: unknown } } | null)
    ?.assistant
  if (!assistant || typeof assistant.tickets !== "boolean") return null
  return assistant.tickets
}
